// Adaptateurs de données partagés pour les trois moteurs de rendu d'arbre
// (Topola, family-chart, Balkan). Construit un modèle « familles » robuste à
// partir de (membres, relations), en gérant notamment les enfants dont les
// parents ne forment PAS un couple déclaré : on crée quand même une famille à
// partir de l'ensemble de leurs parents.
//
// Sémantique des relations (identique à buildTree.ts / lineage.ts) :
//   - 'parent'                : membre_source = parent, membre_cible = enfant.
//   - 'conjoint' / 'ex_conjoint' : couple ; date_debut = mariage.
//   - le sexe ('Homme'/'Femme') distingue mari/épouse ; sinon slots ordonnés.
// Les relations de fratrie (frere/soeur/...) sont volontairement ignorées ici.

import { Membre, Relation } from '../types';
import { JsonGedcomData, JsonIndi, JsonFam } from 'topola';

const PARENT_RELATION = 'parent';
const CONJUGAL_RELATIONS = ['conjoint', 'ex_conjoint'];

/** Représentation « par personne » exploitable par family-chart ET Balkan. */
export interface PersonNode {
  id: number;
  prenom: string;
  nom: string;
  sexe?: string;
  date_naissance?: string;
  date_deces?: string;
  nom_jeune_fille?: string;
  father?: number; // parent masculin (ou 1er slot)
  mother?: number; // parent féminin (ou 2e slot)
  spouses: number[];
  children: number[];
}

interface Family {
  id: string; // 'F1', 'F2', …
  husb?: number;
  wife?: number;
  children: number[];
  marriageDate?: string;
}

interface Model {
  families: Family[];
  famcByChild: Map<number, string>;    // enfant → id de la famille où il est enfant
  famsByParent: Map<number, string[]>; // parent → ids des familles où il est conjoint/parent
  spousesById: Map<number, number[]>;  // personne → ids de ses conjoint(e)s
  memberMap: Map<number, Membre>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function sexToMF(sexe?: string): 'M' | 'F' | undefined {
  if (sexe === 'Homme') return 'M';
  if (sexe === 'Femme') return 'F';
  return undefined;
}

interface SimpleDate {
  year?: number;
  month?: number; // 1-12 (Topola attend un mois 1-based)
  day?: number;
}

function toSimpleDate(s?: string): SimpleDate | undefined {
  if (!s) return undefined;
  const m = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/.exec(s);
  if (!m) return undefined;
  const date: SimpleDate = { year: Number(m[1]) };
  if (m[2]) date.month = Number(m[2]);
  if (m[3]) date.day = Number(m[3]);
  return date;
}

// Répartit un ensemble de parents en slots mari / épouse de façon stable :
// mari = 1er Homme, épouse = 1re Femme, puis remplissage par ordre d'id.
function assignSpousalSlots(
  ids: number[],
  map: Map<number, Membre>,
): { husb?: number; wife?: number } {
  const husb = ids.find(id => map.get(id)?.sexe === 'Homme');
  const wife = ids.find(id => id !== husb && map.get(id)?.sexe === 'Femme');
  const rest = ids.filter(id => id !== husb && id !== wife);
  return {
    husb: husb ?? rest.shift(),
    wife: wife ?? rest.shift(),
  };
}

// ─── Cœur : construction du modèle « familles » ──────────────────────────────

function buildModel(membres: Membre[], relations: Relation[]): Model {
  const memberMap = new Map(membres.map(m => [m.id, m]));
  const exists = (id: number) => memberMap.has(id);

  // Parents déclarés de chaque enfant.
  const parentsOfChild = new Map<number, Set<number>>();
  relations.forEach(r => {
    if (r.type_relation !== PARENT_RELATION) return;
    if (!exists(r.membre_source) || !exists(r.membre_cible)) return;
    if (!parentsOfChild.has(r.membre_cible)) parentsOfChild.set(r.membre_cible, new Set());
    parentsOfChild.get(r.membre_cible)!.add(r.membre_source);
  });

  // Conjoints + dates de mariage.
  const spousesById = new Map<number, Set<number>>();
  const coupleDate = new Map<string, string | undefined>(); // clé 'min-max' → date mariage
  relations.forEach(r => {
    if (!CONJUGAL_RELATIONS.includes(r.type_relation)) return;
    if (!exists(r.membre_source) || !exists(r.membre_cible)) return;
    const a = r.membre_source;
    const b = r.membre_cible;
    if (!spousesById.has(a)) spousesById.set(a, new Set());
    if (!spousesById.has(b)) spousesById.set(b, new Set());
    spousesById.get(a)!.add(b);
    spousesById.get(b)!.add(a);
    const key = [Math.min(a, b), Math.max(a, b)].join('-');
    if (!coupleDate.has(key)) coupleDate.set(key, r.date_debut);
  });

  // Regroupement des enfants par « ensemble de parents ». Deux enfants sont
  // frères/sœurs (même famille) ssi ils ont exactement le même ensemble de
  // parents. Amélioration : un enfant à parent unique dont ce parent a
  // exactement un(e) conjoint(e) est rattaché au couple (comme buildTree.ts).
  const rawFamilies = new Map<string, { parents: number[]; children: Set<number>; marriage?: string }>();
  const ensureFamily = (parentIds: number[]) => {
    const sorted = Array.from(new Set(parentIds)).sort((x, y) => x - y);
    const key = sorted.join('-');
    let fam = rawFamilies.get(key);
    if (!fam) {
      fam = { parents: sorted, children: new Set() };
      rawFamilies.set(key, fam);
    }
    return fam;
  };

  parentsOfChild.forEach((parents, child) => {
    let effective = Array.from(parents);
    if (effective.length === 1) {
      const spouses = spousesById.get(effective[0]);
      if (spouses && spouses.size === 1) effective = [effective[0], Array.from(spouses)[0]];
    }
    ensureFamily(effective).children.add(child);
  });

  // Familles issues des couples (même sans enfant) + report des dates de mariage.
  coupleDate.forEach((date, key) => {
    const [a, b] = key.split('-').map(Number);
    const fam = ensureFamily([a, b]);
    if (date && !fam.marriage) fam.marriage = date;
  });

  // Attribution des ids + slots mari/épouse (ordre déterministe par clé).
  const families: Family[] = [];
  const famcByChild = new Map<number, string>();
  const famsByParent = new Map<number, string[]>();
  let counter = 0;
  Array.from(rawFamilies.keys()).sort().forEach(key => {
    const raw = rawFamilies.get(key)!;
    const famId = 'F' + ++counter;
    const { husb, wife } = assignSpousalSlots(raw.parents, memberMap);
    const children = Array.from(raw.children).sort((a, b) => a - b);
    families.push({ id: famId, husb, wife, children, marriageDate: raw.marriage });
    children.forEach(c => famcByChild.set(c, famId));
    [husb, wife].forEach(p => {
      if (p === undefined) return;
      if (!famsByParent.has(p)) famsByParent.set(p, []);
      famsByParent.get(p)!.push(famId);
    });
  });

  const spousesArr = new Map<number, number[]>();
  spousesById.forEach((set, id) => spousesArr.set(id, Array.from(set).sort((a, b) => a - b)));

  return { families, famcByChild, famsByParent, spousesById: spousesArr, memberMap };
}

// ─── Format Topola natif : { indis, fams } ───────────────────────────────────

export function toTopolaJson(membres: Membre[], relations: Relation[]): JsonGedcomData {
  const model = buildModel(membres, relations);

  const indis: JsonIndi[] = membres.map(m => {
    const indi: JsonIndi = {
      id: 'I' + m.id,
      firstName: m.prenom || '',
      lastName: m.nom || '',
    };
    const sex = sexToMF(m.sexe);
    if (sex) indi.sex = sex;
    if (m.nom_jeune_fille) indi.maidenName = m.nom_jeune_fille;
    const birth = toSimpleDate(m.date_naissance);
    if (birth) indi.birth = { date: birth };
    const death = toSimpleDate(m.date_deces);
    if (death) indi.death = { date: death };
    const fams = model.famsByParent.get(m.id);
    if (fams && fams.length) indi.fams = fams;
    const famc = model.famcByChild.get(m.id);
    if (famc) indi.famc = famc;
    return indi;
  });

  const fams: JsonFam[] = model.families.map(f => {
    const fam: JsonFam = { id: f.id };
    if (f.husb !== undefined) fam.husb = 'I' + f.husb;
    if (f.wife !== undefined) fam.wife = 'I' + f.wife;
    if (f.children.length) fam.children = f.children.map(c => 'I' + c);
    const marriage = toSimpleDate(f.marriageDate);
    if (marriage) fam.marriage = { date: marriage };
    return fam;
  });

  return { indis, fams };
}

// ─── Format « par personne » pour family-chart ET Balkan ─────────────────────

export function toPersonNodes(membres: Membre[], relations: Relation[]): PersonNode[] {
  const model = buildModel(membres, relations);
  const famById = new Map(model.families.map(f => [f.id, f]));

  return membres.map(m => {
    const famcId = model.famcByChild.get(m.id);
    const famc = famcId ? famById.get(famcId) : undefined;

    const childrenSet = new Set<number>();
    (model.famsByParent.get(m.id) || []).forEach(fid => {
      famById.get(fid)?.children.forEach(c => childrenSet.add(c));
    });

    return {
      id: m.id,
      prenom: m.prenom || '',
      nom: m.nom || '',
      sexe: m.sexe,
      date_naissance: m.date_naissance,
      date_deces: m.date_deces,
      nom_jeune_fille: m.nom_jeune_fille,
      father: famc?.husb,
      mother: famc?.wife,
      spouses: model.spousesById.get(m.id) || [],
      children: Array.from(childrenSet).sort((a, b) => a - b),
    };
  });
}

// Id de la première racine (membre sans parent) utile comme point de départ par
// défaut pour les moteurs centrés sur un individu (Topola, family-chart).
export function firstRootId(membres: Membre[], relations: Relation[]): number | null {
  const childIds = new Set(
    relations.filter(r => r.type_relation === PARENT_RELATION).map(r => r.membre_cible),
  );
  const root = membres.find(m => !childIds.has(m.id));
  return root?.id ?? membres[0]?.id ?? null;
}
